import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronRight, Info, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, FormGrid, ModalError } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { kodZnany, nazwaOperacji, podzielOperacje } from '@/lib/kody-operacji';
import { fetchOperacje, zmienOperacje, type CelOperacji } from '@/lib/operations-api';

/**
 * DOZWOLONE OPERACJE SYSTEMOWE — jedno okno dla pracownika i dla grupy.
 *
 * ⚠️ JEDEN KOMPONENT NA OBA CELE, bo różnią się wyłącznie adresem i jednym zdaniem w treści.
 * Dwie kopie rozjechałyby się przy pierwszej poprawce, a różnica byłaby cicha: jedno okno
 * wysyłałoby zmianę inaczej niż drugie.
 *
 * ⚠️ NAZWY TYLKO POTWIERDZONE. Słownik SPS (`Transactions`) ma same kody, bez opisów — więc dla
 * `WN`/`REN`/`IA` pokazujemy nazwę, a dla pozostałych SAM KOD. Rozwinięcie skrótu na wyczucie
 * („MOV = przesunięcie") byłoby domysłem pokazanym jako fakt komuś, kto na tej podstawie nadaje
 * uprawnienie do operacji na magazynie.
 */

export function OperacjeModal({
  cel,
  id,
  islandId,
  canEdit,
  onClose,
  onSaved,
}: {
  cel: CelOperacji;
  id: string;
  islandId: string;
  /** Odczyt dla wszystkich, zapis dla admina — tak jak w API. */
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [wybrane, setWybrane] = useState<Set<string> | null>(null);
  const [saving, setSaving] = useState(false);
  /** Dodatkowe operacje ZWINIĘTE domyślnie — licznik obok mówi, ile z nich jest zaznaczonych. */
  const [dodatkoweOtwarte, setDodatkoweOtwarte] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dane = useQuery({
    queryKey: ['operations', cel, id, islandId],
    queryFn: () => fetchOperacje(cel, id, islandId),
  });

  const poczatkowe = useMemo(
    () => new Set((dane.data?.kody ?? []).filter((k) => k.nadany).map((k) => k.code)),
    [dane.data],
  );

  useEffect(() => {
    if (dane.data) setWybrane(new Set(poczatkowe));
  }, [dane.data, poczatkowe]);

  /**
   * ⚠️ SORTUJEMY PO NAZWIE, NIE PO KODZIE — na prośbę użytkownika, i to jest właściwa kolejność
   * odkąd nazwy w ogóle mamy. Kod jest identyfikatorem dla SPS-a, ale człowiek szuka na tej liście
   * CZYNNOŚCI („zwrot do złomowania"), a nie trzyliterowego skrótu. Alfabet polski, bo nazwy są
   * polskie: bez `localeCompare` „Ł" i „Ż" lądują za „Z".
   */
  const kody = useMemo(() => {
    const lista = dane.data?.kody ?? [];
    return [...lista].sort((a, b) => nazwaOperacji(t, a.code).localeCompare(nazwaOperacji(t, b.code), 'pl'));
  }, [dane.data?.kody, t]);
  /**
   * ⚠️ PODZIAŁ LICZONY Z TEGO, CO WYSPA ZNA, nie z naszej listy — inaczej brak `RN` w słowniku
   * dawałby puste miejsce w sekcji podstawowej. Kolejność podstawowych bierzemy z `PODSTAWOWE`,
   * dodatkowe zostają posortowane po nazwie jak dotąd.
   */
  const { podstawowe, dodatkowe } = useMemo(() => podzielOperacje(kody, (k) => k.code), [kody]);

  const zbior = wybrane ?? poczatkowe;
  const dodane = [...zbior].filter((c) => !poczatkowe.has(c));
  const zdjete = [...poczatkowe].filter((c) => !zbior.has(c));
  const dirty = dodane.length + zdjete.length > 0;
  const zGrup = dane.data?.zGrup ?? [];
  /** Ile pozycji WIDOCZNEJ listy wynika z grup — do jednego zdania nad listą. */
  const zGrupNaLiscie = kody.filter((k) => k.zGrup.length > 0).length;
  /** Kody z grup, których na liście nie ma, bo nie ma ich w słowniku wyspy. */
  const zGrupPozaLista = zGrup.filter((c) => !kody.some((k) => k.code === c));
  /**
   * ⚠️ LICZNIK LICZY TAKŻE UPRAWNIENIA Z GRUP, nie tylko zaznaczone wprost — bo pytanie brzmi
   * „czy pod spodem coś siedzi", a z punktu widzenia automatu nadanie z grupy działa tak samo.
   * Bez tego zwinięta sekcja pokazywałaby zero przy człowieku, który ma z grupy sześć operacji.
   */
  const dodatkoweCzynne = dodatkowe.filter((k) => zbior.has(k.code) || k.zGrup.length > 0).length;
  const pozaSlownikiem = dane.data?.pozaSlownikiem ?? [];

  const przelacz = (code: string) =>
    setWybrane((prev) => {
      const next = new Set(prev ?? poczatkowe);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await zmienOperacje(cel, id, { groupId: islandId, add: dodane, remove: zdjete });
      toast.success(t('ops.saved'));
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  /**
   * JEDEN WIERSZ LISTY — wydzielony, bo od 2026-08-25 rysujemy go w DWÓCH miejscach: w sekcji
   * podstawowej i w zwiniętej sekcji dodatkowych. Dwie kopie tego samego wiersza rozjechałyby się
   * przy pierwszej poprawce, a różnica byłaby cicha — jedna lista zachowywałaby się inaczej niż druga.
   */
  const wiersz = (k: (typeof kody)[number]) => (

                    <label
                      key={k.code}
                      /** Ciasny wiersz: przy 48 pozycjach każdy piksel wysokości to jedno przewinięcie mniej. */
                      className={`flex items-center gap-2 rounded-md px-1.5 py-1 text-sm leading-tight ${
                        canEdit ? 'cursor-pointer hover:bg-accent/50' : 'opacity-70'
                      }`}
                      title={kodZnany(k.code) ? undefined : t('ops.unnamedHint')}
                    >
                      <input
                        type="checkbox"
                        className="size-3.5 shrink-0 accent-[var(--primary)]"
                        checked={zbior.has(k.code)}
                        disabled={!canEdit}
                        onChange={() => przelacz(k.code)}
                      />
                      {/*
                        ⚠️ KOD I NAZWA W JEDNEJ LINII. Kod z przodu, małymi literami i stałą
                        szerokością znaku — czyta się go jak etykietę, a nie jak część zdania,
                        i pozostaje tym, czym jest: wspólnym językiem z SPS-em przez telefon.
                        Nazwa niesie znaczenie i to ona może się urwać, gdy zabraknie miejsca.
                      */}
                      {/*
                        ⚠️ KOD W KOLUMNIE O STAŁEJ SZEROKOŚCI, nie doklejony do nazwy. Kody mają
                        dwa albo trzy znaki (`wi` obok `wrd`), więc przy zwykłym odstępie początki
                        opisów rozjeżdżały się o jeden znak i lista przestawała mieć krawędź,
                        po której da się przejechać wzrokiem. Zgłoszone wprost.
                      */}
                      <span className="w-8 shrink-0 font-mono text-[11px] lowercase text-muted-foreground">
                        {k.code}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{nazwaOperacji(t, k.code)}</span>
                      {/*
                        ⚠️ SKĄD TO UPRAWNIENIE — W WIERSZU, NIE POD LISTĄ. Zgłoszone z użytkowania
                        (2026-08-18): „żeby nie odznaczać na liście uprawnień, które i tak są z grupy".
                        Decyzja „odznaczyć czy nie" podejmowana jest przy konkretnej pozycji, więc
                        informacja musi być przy niej — akapit pod listą kazał składać dwa miejsca
                        w głowie i przy 48 pozycjach nikt tego nie robił.

                        ⚠️ TE SAME SŁOWA I ZNACZNIKI CO NA KARCIE PRACOWNIKA (`profile.direct`,
                        `profile.viaGroup`). Dwa różne opisy tej samej rzeczy na dwóch ekranach
                        czytałoby się jak dwie różne rzeczy.
                      */}
                      {k.zGrup.length > 0 && (
                        <Badge
                          variant="neutral"
                          /** Nazwy grup w podpowiedzi: „z grupy" bez nazwy każe szukać jej na drugim ekranie. */
                          title={`${t('ops.viaGroupHint')} ${k.zGrup.join(', ')}`}
                        >
                          {t('profile.viaGroup')}
                        </Badge>
                      )}
                      {k.nadany && k.zGrup.length === 0 && <Badge variant="default">{t('profile.direct')}</Badge>}
                    </label>
  );

  const naglowek = dane.data ? `${t('ops.title')} — ${dane.data.cel.nazwa}` : t('ops.title');

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title={naglowek}
        description={dane.data ? `${t(cel === 'employee' ? 'ops.card' : 'grp.code')}: ${dane.data.cel.kod}` : undefined}
      >
        <FormGrid>
          {/*
            ⚠️ BEZ AKAPITU WSTĘPNEGO (zgłoszone 01.09.2026). To, że kody idą ze słownika TEJ instalacji
            i dlatego część zostaje surowa, widać po samej liście — a uzasadnienie stoi przy jej
            budowaniu w kodzie.
          */}
          {dane.isPending && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
            </p>
          )}
          {dane.isError && <ModalError>{(dane.error as Error).message}</ModalError>}

          {/* ⚠️ PUSTY SŁOWNIK NIE JEST BŁĘDEM — znaczy, że agent jeszcze nie przyniósł tabeli
              `Transactions`. Trzeba to powiedzieć wprost, bo puste okno wygląda jak awaria. */}
          {dane.data && kody.length === 0 && (
            <p className="flex items-start gap-1.5 text-sm text-muted-foreground">
              <Info className="mt-0.5 size-3.5 shrink-0" />
              {t('ops.noDictionary')}
            </p>
          )}

          {kody.length > 0 && (
            <div className="space-y-2">
              <span className="text-xs text-muted-foreground">
                {t('ops.selected').replace('{n}', String(zbior.size)).replace('{total}', String(kody.length))}
                {/* Ile pozycji tej listy człowiek ma i tak z grup — po to, żeby nie szukał ich po kolei. */}
                {zGrupNaLiscie > 0 && ` · ${t('ops.viaGroupCount').replace('{n}', String(zGrupNaLiscie))}`}
              </span>

              {/*
                ⚠️ PODSTAWOWE BEZ PRZEWIJANIA I BEZ SORTOWANIA ALFABETYCZNEGO. Cztery wiersze widać
                naraz, w kolejności, w jakiej się o nich myśli (wydawanie → uzupełnianie → korekta
                stanów → zwrot). Cały sens tej zmiany polega na tym, żeby po te cztery nie trzeba
                było scrollować listy 47 pozycji, z których prawie żadna nie jest używana.
              */}
              {podstawowe.length > 0 && <div className="rounded-md border border-border p-1">{podstawowe.map(wiersz)}</div>}

              {/*
                ⚠️ LICZNIK NA ZWINIĘTYM NAGŁÓWKU, ŻEBY NIE TRZEBA BYŁO ROZWIJAĆ — zgłoszone wprost.
                Zwinięta sekcja bez licznika kazałaby ją otwierać za każdym razem tylko po to, żeby
                sprawdzić, czy pod spodem czegoś nie ma; a to jest pytanie, które zadaje się ZAWSZE.
              */}
              {dodatkowe.length > 0 && (
                <div className="rounded-md border border-border">
                  <button
                    type="button"
                    onClick={() => setDodatkoweOtwarte((v) => !v)}
                    aria-expanded={dodatkoweOtwarte}
                    className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs text-muted-foreground hover:bg-accent/50"
                  >
                    {dodatkoweOtwarte ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
                    <span className="font-medium">{t('ops.extra')}</span>
                    <span className={dodatkoweCzynne > 0 ? 'text-foreground' : undefined}>
                      {t('ops.extraCount')
                        .replace('{n}', String(dodatkoweCzynne))
                        .replace('{total}', String(dodatkowe.length))}
                    </span>
                  </button>
                  {dodatkoweOtwarte && (
                    <div className="max-h-72 overflow-y-auto border-t border-border p-1">{dodatkowe.map(wiersz)}</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/*
            ⚠️ POD LISTĄ ZOSTAJE TYLKO TO, CZEGO NA LIŚCIE NIE MA. Uprawnienia z grup są teraz
            oznaczone w wierszach, więc powtarzanie ich tutaj byłoby drugim miejscem z tą samą
            treścią. Ale kod nadany grupie, a NIEOBECNY w słowniku wyspy, nie ma swojego wiersza —
            i to jest jedyny przypadek, w którym człowiek dowiedziałby się o nim wyłącznie stąd.
          */}
          {zGrupPozaLista.length > 0 && (
            <div className="rounded-md border border-border bg-muted/40 p-3 text-xs">
              <p className="text-muted-foreground">{t('ops.fromGroupsOutside')}</p>
              <p className="mt-1 flex flex-wrap gap-1">
                {zGrupPozaLista.map((c) => (
                  <Badge key={c} variant="neutral" title={nazwaOperacji(t, c)}>
                    {c}
                  </Badge>
                ))}
              </p>
            </div>
          )}

          {pozaSlownikiem.length > 0 && (
            <div className="rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-3 text-xs">
              <p className="flex items-start gap-1.5">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                <span>{t('ops.outsideDictionary')}</span>
              </p>
              <p className="mt-1 flex flex-wrap gap-1">
                {pozaSlownikiem.map((c) => (
                  <Badge key={c} variant="warning">
                    {c}
                  </Badge>
                ))}
              </p>
            </div>
          )}

          <ModalError>{error}</ModalError>
        </FormGrid>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {canEdit ? t('common.cancel') : t('common.close')}
          </Button>
          {canEdit && kody.length > 0 && (
            <Button type="button" disabled={!dirty || saving} onClick={() => void save()}>
              {saving && <Loader2 className="animate-spin" />}
              {dirty ? `${t('common.save')} (+${dodane.length} −${zdjete.length})` : t('common.save')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
